import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';

const NOTES_MAX_LENGTH = 2000;

interface IEditYearNoteDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    lbId: number;
    notes: string;
    resetTrigger: number;
    onSaved: (notes: string) => void;
}

const EditYearNoteDialog = (props: IEditYearNoteDialogProps) => {
    const { register, getValues, reset } = useForm({ defaultValues: { notes: props.notes ?? '' } });
    const { put, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [charCount, setCharCount] = useState<number>((props.notes ?? '').length);

    // Dialog.tsx keeps this component mounted and toggles visibility, so react-hook-form's
    // state survives between opens - resync to the current year's note (and its length) whenever
    // the parent signals a fresh open, matching AddBenefitDialog.tsx's resetTrigger pattern.
    useEffect(() => {
        const notes = props.notes ?? '';
        reset({ notes });
        setCharCount(notes.length);
    }, [props.resetTrigger]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const onNotesChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
        setCharCount(e.currentTarget.value.length);
    };

    const handleSave = async () => {
        const notes = getValues().notes;

        await put(`/v1.0/landmen/benefits/${props.lbId}/update-employee-benefit-year-note`, notes);

        if (response.ok) {
            props.onSaved(notes);
            props.hideDialog();
            props.showToast('Benefit year note updated successfully');
        } else {
            showToast(`Unable to update benefit year note: ${response}`, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <Dialog title="Edit Year Note" dialogName={props.dialogName} bodyStyle={{ width: '40%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form>
                    <RHFTextArea
                        name="notes"
                        register={register}
                        rows={20}
                        style={{ width: '100%', height: '25rem', resize: 'none' }}
                        defaultValue={props.notes ?? ''}
                        maxLength={NOTES_MAX_LENGTH}
                        onChange={onNotesChange}
                    />
                    <div style={{ textAlign: 'right', color: charCount >= NOTES_MAX_LENGTH ? 'red' : undefined }}>
                        {charCount} / {NOTES_MAX_LENGTH}
                    </div>
                </form>
            </div>
            <div className="button-row" style={{ paddingBottom: '1rem' }}>
                <Button text="Save" onClick={handleSave} intent="primary" />
                <Button text="Cancel" onClick={() => props.hideDialog()} intent="secondary" />
            </div>
        </Dialog>
    );
};

export default EditYearNoteDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/EditYearNoteDialog.tsx